import { Box, CircularProgress, useTheme } from '@mui/material'
import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'
import EmptyScreen from '../../../components/EmptyScreen'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { useDevice } from '../../../hooks/useDevice'
import { useMenuScanFlow } from '../../../hooks/useMenuScanFlow'
import SavedMenuDetailHeader from './components/SavedMenuDetailHeader'
import SavedMenuMetaCard from './components/SavedMenuMetaCard'
import SavedMenuSummaryCard, {
  MenuSummary,
} from './components/SavedMenuSummaryCard'
import SavedMenuDishList, { Dish } from './components/SavedMenuDishList'
import {
  ActionsContainer,
  PageContainer,
  ContentContainer,
  PrimaryActionButton,
} from './styled/SavedMenuDetail.styled'
import { getSavedMenuDetail } from '../../../api/menuScanApi'
import { ROUTES } from '../../../helpers/routes-helper'
import { fetchSavedMenus, selectSavedMenus } from '../../../store/profileSlice'
import { useAppSelector } from '../../../store'
import { errorLog } from '../../../utils/log-helper'

// Interface for saved menu data
interface SavedMenu {
  id: string
  menuName: string
  type: 'User Provided' | 'Scan Only'
  savedDate: string
  imageUrl?: string
  restaurantName?: string
  restaurantAlias?: string
  dishCount: number
  lastUpdated: string
  dishes: Dish[]
  summary?: MenuSummary
}

interface SavedMenuListItem {
  savedId: string
  type: 'RESTAURANT_MENU' | 'SCAN_ONLY'
  savedAt: string
  menuId: string | null
  menuName: string
  restaurantId: string | null
  restaurantName: string | null
  restaurantAlias: string | null
  dishCount: number
  sampleDishes: string[]
  hasImage: boolean
  imageUrl: string | null
}

const SavedMenuDetailPage: React.FC = (): JSX.Element => {
  const theme = useTheme()
  const { menuId } = useParams<{ menuId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { isMediumScreen, dispatch } = useAppUtilities()
  const { isHybrid } = useDevice()
  const { startScan, uploading } = useMenuScanFlow()
  const savedMenusState = useAppSelector(selectSavedMenus)
  const selectedFromState = (location.state as { savedMenuItem?: SavedMenuListItem } | undefined)?.savedMenuItem
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [savedMenu, setSavedMenu] = useState<SavedMenu | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const handleScanAnother = (): void => {
    if (isHybrid || Capacitor.isNativePlatform()) {
      void (async (): Promise<void> => {
        try {
          const photo = await Camera.getPhoto({
            quality: 90,
            resultType: CameraResultType.DataUrl,
            source: CameraSource.Camera,
            allowEditing: false,
          })
          if (!photo.dataUrl) return
          const res = await fetch(photo.dataUrl)
          const blob = await res.blob()
          const file = new File(
            [blob],
            `menu-${Date.now()}.jpg`,
            { type: blob.type || 'image/jpeg' },
          )
          void startScan(file).catch(() => {})
        } catch (e) {
          errorLog('[SavedMenuDetail] Camera error', e)
        }
      })()
    } else {
      fileInputRef.current?.click()
    }
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    void startScan(file).catch(() => {})
  }
  useEffect(() => {
    const fetchMenuDetail = async (): Promise<void> => {
      try {
        setIsLoading(true)
        setError(null)

        if (!menuId) {
          setError('Menu not found')
          setSavedMenu(null)
          return
        }

        let selected: SavedMenuListItem | undefined = selectedFromState
        if (!selected) {
          let sourceItems = savedMenusState.items
          if (savedMenusState.items.length === 0) {
            const action = await dispatch(fetchSavedMenus({ page: 0, size: 50, sort: 'recent' }))
            if (fetchSavedMenus.fulfilled.match(action)) {
              sourceItems = action.payload.items
            }
          }
          selected = sourceItems.find((item) => (item.menuId ?? item.savedId) === menuId)
        }

        if (!selected) {
          setError('Saved menu not found')
          setSavedMenu(null)
          return
        }

        // NEW: Use unified endpoint for all saved menu types
        const detail = await getSavedMenuDetail(selected.savedId)

        // Map dishes to page format
        const dishes: Dish[] = detail.dishes.map((d, idx) => ({
          id: d.dishId ?? `${detail.savedId}-${idx}`,
          name: d.name ?? 'Unnamed dish',
          description: d.description ?? '',
          price: d.price ? `${d.price}` : undefined,
          section: d.section ?? null,
          imageUrl: d.imageUrl ?? undefined,
        }))

        // Map summary (already in correct format)
        const summary: MenuSummary | undefined = detail.summary
          ? {
              quickTake: detail.summary.quickTake || undefined,
              goodFor: detail.summary.goodFor || undefined,
              watchOut: detail.summary.watchOut || undefined,
              friendPick: detail.summary.friendPick || undefined,
            }
          : undefined

        const savedDate = new Date(detail.savedAt)
        const now = new Date()
        const diffDays = Math.floor((now.getTime() - savedDate.getTime()) / (1000 * 60 * 60 * 24))
        const relative =
          diffDays <= 0 ? 'Today' : diffDays === 1 ? 'Yesterday' : `${diffDays} days ago`

        const savedMenu: SavedMenu = {
          id: detail.savedId,
          menuName: detail.menuName,
          type: detail.type === 'SCAN_ONLY' ? 'Scan Only' : 'User Provided',
          savedDate: relative,
          imageUrl: detail.imageUrl ?? undefined,
          restaurantName: detail.restaurantName ?? undefined,
          restaurantAlias: detail.restaurantAlias ?? undefined,
          dishCount: dishes.length,
          lastUpdated: relative,
          dishes,
          summary,
        }
        setSavedMenu(savedMenu)
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            ?? 'Failed to load menu details. Try again.'
            : 'Failed to load menu details. Try again.',
        )
      } finally {
        setIsLoading(false)
      }
    }

    if (menuId) {
      void fetchMenuDetail()
    } else {
      setIsLoading(false)
      setSavedMenu(null)
      setError('Menu not found')
    }
  }, [menuId, selectedFromState, dispatch, savedMenusState.items])

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  // Error state
  if (!savedMenu) {
    return (
      <Box
        sx={{
          padding: { xs: '16px', sm: '20px', md: '24px' },
          maxWidth: '1000px',
          margin: '0 auto',
        }}
      >
        <EmptyScreen
          mainText="Unable to load menu"
          secondaryText={error ?? 'Menu not found'}
          imageUrl="/images/empty-screen.svg"
          containerHeight={isMediumScreen ? '80vh' : '100vh'}
        >
          <button
            onClick={() => navigate(`${ROUTES.PROFILE}?tab=Saved Menus`)}
            style={{
              marginTop: '16px',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: theme.palette.primary.main,
              color: '#fff',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Back to Saved Menus
          </button>
        </EmptyScreen>
      </Box>
    )
  }

  return (
    <PageContainer>
      <Helmet>
        <title>Resactly | {savedMenu.menuName}</title>
      </Helmet>
      {/* Header */}
      <SavedMenuDetailHeader
        menuName={savedMenu.menuName}
        type={savedMenu.type}
        savedDate={savedMenu.savedDate}
      />

      {/* Content */}
      <ContentContainer>
        {/* Meta card */}
        <SavedMenuMetaCard
          imageUrl={savedMenu.imageUrl}
          restaurantName={savedMenu.restaurantName}
          dishCount={savedMenu.dishCount}
          lastUpdated={savedMenu.lastUpdated}
        />

        {/* Summary card (optional) */}
        <SavedMenuSummaryCard summary={savedMenu.summary} />

        {/* Dishes list */}
        <SavedMenuDishList dishes={savedMenu.dishes} restaurantId={savedMenu.restaurantAlias ?? undefined} menuId={menuId} />

        {/* Scan again CTA */}
        <ActionsContainer>
          <PrimaryActionButton onClick={handleScanAnother} disabled={uploading}>
            {uploading ? 'Loading…' : 'Try another menu'}
          </PrimaryActionButton>
        </ActionsContainer>
      </ContentContainer>

      {/* Hidden file input for web */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={uploading}
      />
    </PageContainer>
  )
}

export default SavedMenuDetailPage
